/**
 * Business rule enforcement and validation
 * Functions that implement core business logic for task management
 */

import { TodoTree, TodoItem } from '../../types/todo';
import { getItemById } from './queries';

/**
 * Checks if all direct children of a parent node are marked as completed
 *
 * This is a critical business rule enforcer that prevents parents from being marked
 * as complete when they still have incomplete child tasks. It's used throughout the
 * application to maintain data integrity in the hierarchical task structure.
 *
 * @param tree - The flat todo tree structure
 * @param parentId - UUID of the parent node to check
 * @returns `true` if all children are completed or if there are no children, `false` otherwise
 *
 * @example
 * ```typescript
 * // Prevent marking parent as complete if children are incomplete
 * if (!areAllChildrenCompleted(tree, parentId)) {
 *   showWarning("Complete child tasks first");
 *   return;
 * }
 * toggleDone(parentId);
 * ```
 */
export function areAllChildrenCompleted(tree: TodoTree, parentId: string): boolean {
  const parent = getItemById(tree, parentId);
  if (!parent || parent.children.length === 0) {
    return true; // No children means all children are "completed"
  }

  return parent.children.every(childId => {
    const child = getItemById(tree, childId);
    return child?.done === true;
  });
}

/**
 * Gets all incomplete child tasks of a parent node
 *
 * This function is the counterpart to `areAllChildrenCompleted` and is used to provide
 * detailed feedback to users when they try to complete a parent task with incomplete children.
 * It filters out null references and completed tasks to return only actionable items.
 *
 * @param tree - The flat todo tree structure
 * @param parentId - UUID of the parent node to check
 * @returns Array of incomplete child objects with their IDs and TodoItem data
 *
 * @example
 * ```typescript
 * // Show user which tasks need completion
 * const incompleteChildren = getIncompleteChildren(tree, parentId);
 * const taskNames = incompleteChildren.map(child => child.item.name).join(', ');
 * showWarning(`Complete these tasks first: ${taskNames}`);
 * ```
 */
export function getIncompleteChildren(tree: TodoTree, parentId: string): Array<{ id: string; item: TodoItem }> {
  const parent = getItemById(tree, parentId);
  if (!parent) {
    return [];
  }

  return parent.children
    .map(childId => {
      const item = getItemById(tree, childId);
      return item ? { id: childId, item } : null;
    })
    .filter((child): child is { id: string; item: TodoItem } => child !== null && !child.item.done);
}

/**
 * Recursively finds all ancestor node IDs for a given target node
 *
 * This function traverses up the tree hierarchy to find all parent, grandparent,
 * and higher-level ancestor nodes. It's used when cascading state changes need to
 * propagate through the entire ancestry chain (e.g., when adding a child to a
 * completed parent marks all ancestors as incomplete).
 *
 * @param tree - The flat todo tree structure
 * @param targetId - UUID of the node to find ancestors for
 * @returns Array of ancestor node UUIDs, from direct parent to root
 *
 * @example
 * ```typescript
 * // Mark all ancestors as incomplete when adding a child
 * const ancestorIds = findAllParentIds(tree, parentId);
 * ancestorIds.forEach(ancestorId => {
 *   updateItem(ancestorId, { done: false });
 * });
 * ```
 */
export function findAllParentIds(tree: TodoTree, targetId: string): string[] {
  const parents: string[] = [];

  for (const [parentId, parentItem] of Object.entries(tree)) {
    if (parentItem.children.includes(targetId)) {
      parents.push(parentId);
      // Recursively find grandparents
      parents.push(...findAllParentIds(tree, parentId));
    }
  }

  return parents;
}

/**
 * Checks if a node can have a time estimate
 * Leaf nodes (no children) OR parent nodes where all children are done can have time estimates
 *
 * @param tree - The flat todo tree structure
 * @param nodeId - UUID of the node to check
 * @returns `true` if the node is a leaf OR all its children are done, `false` otherwise
 */
export function canHaveTimeEstimate(tree: TodoTree, nodeId: string): boolean {
  const node = getItemById(tree, nodeId);
  if (!node) return false;

  // Leaf nodes can always have estimates
  if (node.children.length === 0) return true;

  // Parent nodes can have estimates if all children are done
  return areAllChildrenCompleted(tree, nodeId);
}

/**
 * Checks if a child can be added to a node
 * Children cannot be added to nodes that have a time estimate
 *
 * @param tree - The flat todo tree structure
 * @param nodeId - UUID of the node to check
 * @returns `true` if the node has no time estimate (or estimate is undefined), `false` otherwise
 */
export function canAddChildToNode(tree: TodoTree, nodeId: string): boolean {
  const node = getItemById(tree, nodeId);
  if (!node) return false;
  return node.timeEstimate === undefined;
}
